import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Tooltip as UDSTooltip } from '@telus-uds/components-web';

// Force all nested elements to inherit the tooltip's white text color.
// UDS Paragraph/Typography/List components set dark text colors that conflict
// with the tooltip's dark background.
const InheritColor = styled.div`
  color: inherit;
  & * {
    color: inherit !important;
  }
  & a, & a * {
    color: #7C5CFC !important;
    text-decoration: underline;
  }
`;

// UDS renders the popup inside a portal whose ancestors all have
// `pointer-events: none`, so clicks pass straight through the popup to the page
// behind it and its links can never be pressed. Re-enable pointer events on the
// popup content.
const PopupContent = styled.div`
  pointer-events: auto;
`;

// UDS Tooltip also closes itself when its trigger button loses focus. Pressing a
// link inside the popup would move focus away on mousedown and unmount the popup
// before the click lands. Cancelling the mousedown default keeps focus on the
// trigger while click/navigation still work.
const keepTriggerFocused = event => event.preventDefault();

const TooltipImage = styled.img`
  display: block;
  max-width: 100%;
  height: auto;
  margin: 6px 0;
`;

const TooltipParagraph = styled.p`
  margin: 0 0 8px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const normalizeUrl = url => {
  if (!url || typeof url !== 'string') return '';
  if (url.startsWith('//')) return `https:${url}`;
  return url;
};

// Invisible characters (soft hyphen, zero-width space/joiners, word joiner, BOM)
// often arrive from copy-pasted Contentful copy and give the browser a hidden
// break point, which wraps words mid-word (e.g. "p" / "age").
const INVISIBLE_BREAK_CHARS = /[\u00AD\u200B-\u200D\u2060\uFEFF]/g;

export const stripInvisibleBreakChars = text =>
  typeof text === 'string' ? text.replace(INVISIBLE_BREAK_CHARS, '') : text;

const renderInlineContent = (rawText, keyPrefix = 'tooltip-inline') => {
  if (typeof rawText !== 'string') return rawText;

  const text = stripInvisibleBreakChars(rawText);
  const tokenRegex = /!\[([^\]]*)\]\(([^)]+)\)|\[([^\]]+)\]\(([^)]+)\)|(https?:\/\/[^\s]+|\/\/[^\s]+)/g;
  const parts = [];
  let lastIndex = 0;
  let match = tokenRegex.exec(text);
  let tokenIndex = 0;

  while (match !== null) {
    const [fullMatch, imageAlt, imageUrl, linkText, linkUrl, plainUrl] = match;
    const start = match.index;

    if (start > lastIndex) {
      parts.push(text.substring(lastIndex, start));
    }

    if (imageUrl) {
      parts.push(
        <TooltipImage
          alt={imageAlt || 'tooltip image'}
          key={`${keyPrefix}-img-${tokenIndex}`}
          src={normalizeUrl(imageUrl)}
        />
      );
    } else if (linkUrl) {
      parts.push(
        <a
          href={normalizeUrl(linkUrl)}
          target="_blank"
          rel="noopener noreferrer"
          key={`${keyPrefix}-link-${tokenIndex}`}
        >
          {linkText}
        </a>
      );
    } else if (plainUrl) {
      const safeUrl = normalizeUrl(plainUrl);
      parts.push(
        <a
          href={safeUrl}
          target="_blank"
          rel="noopener noreferrer"
          key={`${keyPrefix}-url-${tokenIndex}`}
        >
          {safeUrl}
        </a>
      );
    }

    tokenIndex += 1;
    lastIndex = start + fullMatch.length;
    match = tokenRegex.exec(text);
  }

  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts.length ? parts : text;
};

const renderTextNode = (node, keyPrefix) => {
  const value = renderInlineContent(node.value || '', keyPrefix);
  const marks = node.marks || [];
  const isBold = marks.some(mark => mark.type === 'bold');

  if (isBold) {
    return <strong key={`${keyPrefix}-bold`}>{value}</strong>;
  }

  return value;
};

const renderRichTextNode = (node, index) => {
  if (!node) return null;

  if (node.nodeType === 'embedded-asset-block') {
    const target = node.data && node.data.target;
    const fields = target && target.fields;
    const file = fields && fields.file;
    const title = fields && fields.title;
    const src = normalizeUrl(file && file.url);

    if (!src) return null;

    return (
      <TooltipImage
        key={`asset-${index}`}
        src={src}
        alt={title || 'tooltip image'}
      />
    );
  }

  if (node.nodeType === 'paragraph') {
    const content = node.content || [];

    return (
      <TooltipParagraph key={`paragraph-${index}`}>
        {content.map((child, childIndex) => {
          if (child.nodeType === 'text') {
            return renderTextNode(
              child,
              `paragraph-${index}-text-${childIndex}`
            );
          }

          return null;
        })}
      </TooltipParagraph>
    );
  }

  return null;
};

// Render plain strings, inline markdown/links, or Contentful rich text documents.
const renderContent = value => {
  if (typeof value === 'string') {
    return renderInlineContent(value);
  }

  if (
    value &&
    value.nodeType === 'document' &&
    Array.isArray(value.content)
  ) {
    return value.content.map((node, index) =>
      renderRichTextNode(node, index)
    );
  }

  return value;
};

// Map custom direction values to UDS Tooltip position values
const positionMap = {
  auto: 'auto',
  left: 'left',
  right: 'right',
  top: 'above',
  bottom: 'below'
};

const Tooltip = ({ children, direction = 'auto', copy }) => {
  const parsed = renderContent(children);
  const position = positionMap[direction] || 'auto';

  // UDS Tooltip has no close-on-scroll behavior and no controlled open prop -
  // once opened (click-to-toggle) it stays open and just floats disconnected
  // from its trigger as the page scrolls. Force it closed on a real scroll by
  // remounting it (resets its internal isOpen state back to false).
  const [remountKey, setRemountKey] = useState(0);
  useEffect(() => {
    const handleScroll = (event) => {
      if (!event.isTrusted) return;
      setRemountKey(k => k + 1);
    };
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, []);

  // Wrap non-string content to force color inheritance inside the dark tooltip popup.
  // UDS components like Paragraph/Typography set their own dark text colors which
  // conflict with the tooltip's white-on-dark background.
  const content = (
    <PopupContent onMouseDown={keepTriggerFocused}>
      {typeof parsed === 'string' ? (
        parsed
      ) : (
        <InheritColor>{parsed}</InheritColor>
      )}
    </PopupContent>
  );

  return (
    <UDSTooltip
      key={remountKey}
      inline
      content={content}
      position={position}
      {...(copy ? { copy } : {})}
    />
  );
};

Tooltip.propTypes = {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.object]).isRequired,
  direction: PropTypes.string,
  copy: PropTypes.oneOf(['en', 'fr'])
};

export default Tooltip;
